
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import ThreadCard from './ThreadCard';
import ThreadDetail from './ThreadDetail';

interface Thread {
  id: string;
  title: string;
  content: string;
  board: 'feedback' | 'theory' | 'fanart';
  is_pinned: boolean;
  created_at: string;
  author: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
  reply_count: number;
  like_count: number;
}

interface ThreadListProps {
  board: 'feedback' | 'theory' | 'fanart';
  sortBy: 'latest' | 'most_replies' | 'most_liked';
}

export default function ThreadList({ board, sortBy }: ThreadListProps) {
  // Fixed generic syntax: useState<Thread[]>([])
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);

  useEffect(() => {
    fetchThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, sortBy]);

  const fetchThreads = async () => {
    setLoading(true);
    try {
      // Build base query
      const baseQuery = supabase
        .from('forum_threads')
        .select(`
          id,
          title,
          content,
          board,
          is_pinned,
          created_at,
          author:profiles!forum_threads_author_id_fkey (
            id,
            username,
            avatar_url
          )
        `)
        .eq('board', board);

      // Execute query
      const { data: threadsData, error: threadsError } = await baseQuery;

      if (threadsError) throw threadsError;

      // Handle empty result early
      if (!threadsData || threadsData.length === 0) {
        setThreads([]);
        return;
      }

      // Gather IDs for batch count queries
      const threadIds = threadsData.map((t: any) => t.id);

      // Reply counts
      const { data: replyCounts, error: replyError } = await supabase
        .from('forum_replies')
        .select('thread_id')
        .in('thread_id', threadIds);
      if (replyError) throw replyError;

      // Like counts
      const { data: likeCounts, error: likeError } = await supabase
        .from('forum_thread_likes')
        .select('thread_id')
        .in('thread_id', threadIds);
      if (likeError) throw likeError;

      // Attach counts to each thread
      const threadsWithCounts = (threadsData as any[]).map((thread) => ({
        ...thread,
        reply_count:
          replyCounts?.filter((r: any) => r.thread_id === thread.id).length || 0,
        like_count:
          likeCounts?.filter((l: any) => l.thread_id === thread.id).length || 0,
      }));

      // Sorting (pinned first, then by selected criteria)
      const sortedThreads = [...threadsWithCounts].sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;

        if (sortBy === 'most_replies') {
          return b.reply_count - a.reply_count;
        } else if (sortBy === 'most_liked') {
          return b.like_count - a.like_count;
        }
        // Default: latest
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      setThreads(sortedThreads);
    } catch (error) {
      console.error('Error fetching threads:', error);
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (threadId: string) => {
    setExpandedThreadId((prev) => (prev === threadId ? null : threadId));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-zinc-900/50 border border-red-900/20 p-6 animate-pulse"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-zinc-800"></div>
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-zinc-800 rounded w-1/4"></div>
                <div className="h-6 bg-zinc-800 rounded w-3/4"></div>
                <div className="h-4 bg-zinc-800 rounded w-full"></div>
                <div className="h-4 bg-zinc-800 rounded w-2/3"></div>
                <div className="flex gap-6 mt-4">
                  <div className="h-4 bg-zinc-800 rounded w-20"></div>
                  <div className="h-4 bg-zinc-800 rounded w-20"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
          <i className={`${getBoardIcon(board)} text-5xl text-red-900/30`}></i>
        </div>
        <h3 className="text-xl font-['Cinzel'] text-zinc-400 mb-2">
          {getEmptyStateTitle(board)}
        </h3>
        <p className="text-zinc-500 font-['Crimson_Text'] max-w-md mx-auto">
          {getEmptyStateDescription(board)}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {threads.map((thread) => (
        <div key={thread.id}>
          <ThreadCard
            id={thread.id}
            title={thread.title}
            content={thread.content}
            author={thread.author}
            isPinned={thread.is_pinned}
            replyCount={thread.reply_count}
            likeCount={thread.like_count}
            createdAt={thread.created_at}
            board={thread.board}
            isExpanded={expandedThreadId === thread.id}
            onToggleExpand={() => handleToggleExpand(thread.id)}
          />
          {expandedThreadId === thread.id && (
            <ThreadDetail
              threadId={thread.id}
              content={thread.content}
              onClose={() => setExpandedThreadId(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/* Helper functions remain unchanged */
function getBoardIcon(board: string): string {
  switch (board) {
    case 'feedback':
      return 'ri-chat-heart-line';
    case 'theory':
      return 'ri-question-mark';
    case 'fanart':
      return 'ri-image-line';
    default:
      return 'ri-chat-3-line';
  }
}

function getEmptyStateTitle(board: string): string {
  switch (board) {
    case 'feedback':
      return 'No Feedback Yet';
    case 'theory':
      return 'No Theories Yet';
    case 'fanart':
      return 'No Art Shared Yet';
    default:
      return 'No Threads Yet';
  }
}

function getEmptyStateDescription(board: string): string {
  switch (board) {
    case 'feedback':
      return 'Be the first to share your thoughts on a story. Writers are waiting for your insights from the shadows.';
    case 'theory':
      return 'The mysteries remain unsolved. Be the first to unravel the hidden truths lurking within these tales.';
    case 'fanart':
      return 'The gallery awaits its first masterpiece. Share your dark visions inspired by these haunting stories.';
    default:
      return 'Be the first to start a conversation in the darkness.';
  }
}
